/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import 'bootstrap/dist/css/bootstrap.css';
import { ColorModeScript } from '@chakra-ui/react';
import React, { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import App, { customTheme } from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorker from './serviceWorker';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getRemoteConfig } from "firebase/remote-config";
import { BrowserRouter as Router } from 'react-router-dom';
import { PublicKey } from '@solana/web3.js';
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react';
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  SolletWalletAdapter,
  SolletExtensionWalletAdapter,
  CoinbaseWalletAdapter,
  BraveWalletAdapter,
} from '@solana/wallet-adapter-wallets';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import react from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { getNetwork, isDev } from './api/qdot';
import './index.css';

const firebaseConfig = isDev()
  ? {
      apiKey: 'AIzaSyDxzaxPJNQcO3aFQA_MZVRFF8CXYWavvDw',
      authDomain: 'cleverchuk-fe49e.firebaseapp.com',
      projectId: 'cleverchuk-fe49e',
      storageBucket: 'cleverchuk-fe49e.appspot.com',
      messagingSenderId: '728386147908',
      appId: '1:728386147908:web:7d4c29b4378ca4226f8e11',
      measurementId: 'G-VDQS8683Y5',
    }
  : {
      apiKey: 'AIzaSyDCWoLDC6-nOQgRc0mdHSDdwF504cwCJ4E',
      authDomain: 'musicq-7f247.firebaseapp.com',
      projectId: 'musicq-7f247',
      storageBucket: 'musicq-7f247.appspot.com',
      messagingSenderId: '1083649111356',
      appId: '1:1083649111356:web:4fd80e1845bdaeb47a1a39',
      measurementId: 'G-BQHKYW2BGF',
    };

const app = initializeApp(firebaseConfig);
export const storage = getStorage(app);
export const db = getFirestore(app);
export const remoteConfig = getRemoteConfig(app)

!isDev() && enableIndexedDbPersistence(db);
export const analytics = !isDev() && getAnalytics(app);

export const appCheck =
  !isDev() &&
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(
      '6LfDdV8gAAAAAJX5SL8Dn9X-fgCLnm6HPp155gM_'
    ),
    isTokenAutoRefreshEnabled: true,
  });
export const lordWallet = new PublicKey(
  '4iEM49kSmsfQxpvAx2V6LyzN7FTJkqhcEQFPFkWLe1tG'
);

const audioContext = new (window.AudioContext || window.webkitAudioContext)();
export const PlayerAudioContext = react.createContext(audioContext);
function AppRoot({ children }) {
  const network = useMemo(() => getNetwork(), []);
  const endpoint = useMemo(
    () => network,
    [network]
  );
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new SolletWalletAdapter(),
      new SolletExtensionWalletAdapter(),
      new CoinbaseWalletAdapter(),
      new BraveWalletAdapter(),
    ],
    [network]
  );

  return (
    <ChakraProvider theme={customTheme}>
      <ConnectionProvider endpoint={endpoint}>
        <WalletProvider wallets={wallets} autoConnect>
          <WalletModalProvider>
            <PlayerAudioContext.Provider value={audioContext}>
              {children}
            </PlayerAudioContext.Provider>
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </ChakraProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <StrictMode>
    <ColorModeScript />
    <Router>
      <AppRoot>
        <App />
      </AppRoot>
    </Router>
  </StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorker.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
